import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  AccountBalanceWallet,
  AssignmentTurnedIn,
  Autorenew,
  Download,
  PictureAsPdf,
  ReceiptLong,
  Refresh,
  WarningAmber
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import projectMoneyTrailService from '../../services/projectMoneyTrailService';
import { brand, status, text, border, bg } from '../../theme/colors';

const formatCurrency = (amount, currency = 'USD') => {
  const numeric = Number(amount || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(Number.isNaN(numeric) ? 0 : numeric);
};

const formatDate = (date) => {
  if (!date) return 'Not set';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Not set';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const statusColors = {
  pending: 'warning',
  processing: 'info',
  failed: 'error',
  completed: 'success',
  approved: 'info',
  held_in_escrow: 'warning'
};

const sourceLabels = {
  milestone: 'Milestone',
  payment_recipient: 'Payment split',
  material_order: 'Materials',
  boq_labor_payment: 'Labor'
};

const safeFilePart = (value) => {
  return String(value || 'project')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'project';
};

const csvEscape = (value) => {
  const textValue = value === null || value === undefined ? '' : String(value);
  return `"${textValue.replace(/"/g, '""')}"`;
};

const downloadBlob = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ProjectMoneyTrail = ({ projects = [], initialProjectId = '', onProjectChange }) => {
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId || '');
  const [moneyTrail, setMoneyTrail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const normalizedProjects = useMemo(() => {
    return projects
      .filter((project) => project?.id || project?.projectId || project?.project_id)
      .map((project) => ({
        ...project,
        id: project.id || project.projectId || project.project_id,
        title: project.title || project.name || `Project ${project.id || project.projectId || project.project_id}`
      }));
  }, [projects]);

  useEffect(() => {
    if (initialProjectId) {
      setSelectedProjectId(initialProjectId);
    }
  }, [initialProjectId]);

  useEffect(() => {
    if (!selectedProjectId && normalizedProjects.length === 1) {
      setSelectedProjectId(normalizedProjects[0].id);
    }
  }, [normalizedProjects, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setMoneyTrail(null);
      return;
    }

    loadMoneyTrail(selectedProjectId);
  }, [selectedProjectId]);

  const loadMoneyTrail = async (projectId = selectedProjectId) => {
    if (!projectId) return;
    setLoading(true);
    setError('');

    try {
      const data = await projectMoneyTrailService.getMoneyTrail(projectId);
      setMoneyTrail(data);
    } catch (err) {
      setMoneyTrail(null);
      setError(err?.response?.data?.message || err.message || 'Failed to load money trail');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (event) => {
    const projectId = event.target.value;
    setSelectedProjectId(projectId);
    if (onProjectChange) onProjectChange(projectId);
  };

  const summary = moneyTrail?.summary || {};
  const currency = moneyTrail?.currency || 'USD';
  const outstanding = moneyTrail?.outstandingRecipients || [];
  const hasWarnings = (summary.reconciliationWarnings || []).length > 0;
  const exportFilenameBase = moneyTrail
    ? `${safeFilePart(moneyTrail.projectTitle)}-money-trail-${new Date().toISOString().slice(0, 10)}`
    : 'project-money-trail';

  const cards = [
    {
      label: 'Funded',
      value: summary.totalFunded,
      helper: 'Total added to project wallets',
      icon: <AccountBalanceWallet />,
      color: brand.primary
    },
    {
      label: 'Released',
      value: summary.totalReleased,
      helper: 'Milestones and materials paid out',
      icon: <AssignmentTurnedIn />,
      color: status.success
    },
    {
      label: 'Outstanding',
      value: summary.totalOutstanding,
      helper: `${outstanding.length} open payment item${outstanding.length === 1 ? '' : 's'}`,
      icon: <Autorenew />,
      color: status.warning
    },
    {
      label: 'Available',
      value: summary.availableBalance,
      helper: 'Not released or held for materials',
      icon: <ReceiptLong />,
      color: status.info
    }
  ];

  const buildStatementRows = () => {
    const summaryRows = [
      ['Section', 'Label', 'Value', 'Currency', 'Belongs To', 'Status', 'Due Date', 'Reason'],
      ['Summary', 'Total funded', summary.totalFunded || 0, currency, '', '', '', 'Total added to project wallets'],
      ['Summary', 'Total released', summary.totalReleased || 0, currency, '', '', '', 'Milestones and materials paid out'],
      ['Summary', 'Outstanding', summary.totalOutstanding || 0, currency, '', '', '', 'Open payments by recipient'],
      ['Summary', 'Available balance', summary.availableBalance || 0, currency, '', '', '', 'Not released or held'],
      ['Summary', 'Held for materials', summary.heldForMaterials || 0, currency, '', '', '', 'Material order holds'],
      ['Summary', 'Milestones released', summary.releasedMilestonePayments || 0, currency, '', '', '', 'Released milestone payments'],
      ['Summary', 'Materials released', summary.releasedMaterialPayments || 0, currency, '', '', '', 'Released material payments']
    ];

    const outstandingRows = outstanding.map((item) => [
      'Outstanding',
      sourceLabels[item.source] || item.source || '',
      item.amount || 0,
      item.currency || currency,
      item.recipientName || '',
      (item.status || '').replace(/_/g, ' '),
      formatDate(item.dueDate),
      item.reason || ''
    ]);

    return [...summaryRows, ...outstandingRows];
  };

  const handleExportCsv = () => {
    if (!moneyTrail) return;
    const metadataRows = [
      ['Eqaya Project Money Trail'],
      ['Project', moneyTrail.projectTitle || selectedProjectId],
      ['Project ID', moneyTrail.projectId || selectedProjectId],
      ['Generated', formatDate(moneyTrail.generatedAt)],
      []
    ];

    const csv = [...metadataRows, ...buildStatementRows()]
      .map((row) => row.map(csvEscape).join(','))
      .join('\n');

    downloadBlob(csv, `${exportFilenameBase}.csv`, 'text/csv;charset=utf-8;');
  };

  const handleExportPdf = () => {
    if (!moneyTrail) return;

    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    let y = 18;

    const ensureSpace = (height = 12) => {
      if (y + height > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }
    };

    const addText = (textValue, x, options = {}) => {
      const {
        fontSize = 10,
        fontStyle = 'normal',
        color = [30, 41, 59],
        width = maxWidth,
        lineHeight = 5,
        align
      } = options;

      pdf.setFont('helvetica', fontStyle);
      pdf.setFontSize(fontSize);
      pdf.setTextColor(...color);
      const lines = pdf.splitTextToSize(String(textValue || ''), width);
      pdf.text(lines, x, y, align ? { align } : undefined);
      y += Math.max(lineHeight, lines.length * lineHeight);
    };

    pdf.setFillColor(13, 148, 136);
    pdf.rect(0, 0, pageWidth, 32, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('Eqaya Project Money Trail', margin, 14);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`Generated ${formatDate(moneyTrail.generatedAt || new Date())}`, margin, 23);

    y = 42;
    addText(moneyTrail.projectTitle || 'Project', margin, { fontSize: 14, fontStyle: 'bold', lineHeight: 7 });
    addText(`Project ID: ${moneyTrail.projectId || selectedProjectId}`, margin, {
      fontSize: 9,
      color: [100, 116, 139]
    });

    y += 4;
    const summaryItems = [
      ['Funded', summary.totalFunded],
      ['Released', summary.totalReleased],
      ['Outstanding', summary.totalOutstanding],
      ['Available', summary.availableBalance],
      ['Held for materials', summary.heldForMaterials],
      ['Milestones released', summary.releasedMilestonePayments],
      ['Materials released', summary.releasedMaterialPayments]
    ];

    summaryItems.forEach(([label, value], index) => {
      ensureSpace(12);
      const x = margin + (index % 2) * ((maxWidth / 2) + 3);
      if (index % 2 === 0 && index > 0) y += 3;
      const currentY = y;
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(x, currentY - 5, (maxWidth / 2) - 3, 15, 2, 2);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      pdf.text(label, x + 4, currentY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(30, 41, 59);
      pdf.text(formatCurrency(value, currency), x + 4, currentY + 6);
      if (index % 2 === 1) y += 16;
    });

    if (summaryItems.length % 2 === 1) y += 16;
    y += 4;

    if (hasWarnings) {
      ensureSpace(18);
      pdf.setFillColor(255, 251, 235);
      pdf.roundedRect(margin, y - 4, maxWidth, 16, 2, 2, 'F');
      addText((summary.reconciliationWarnings || []).join(' '), margin + 4, {
        width: maxWidth - 8,
        color: [146, 64, 14],
        fontSize: 8,
        lineHeight: 4
      });
      y += 4;
    }

    ensureSpace(16);
    addText('Outstanding Payments', margin, { fontSize: 12, fontStyle: 'bold', lineHeight: 7 });

    if (outstanding.length === 0) {
      addText('No outstanding project payments.', margin, { color: [100, 116, 139] });
    } else {
      outstanding.forEach((item) => {
        ensureSpace(24);
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, y - 2, pageWidth - margin, y - 2);
        addText(`${item.recipientName || 'Recipient'} - ${formatCurrency(item.amount, item.currency || currency)}`, margin, {
          fontSize: 10,
          fontStyle: 'bold',
          lineHeight: 5
        });
        addText(`${sourceLabels[item.source] || item.source} | ${(item.status || 'pending').replace(/_/g, ' ')} | Due: ${formatDate(item.dueDate)}`, margin, {
          fontSize: 8,
          color: [100, 116, 139],
          lineHeight: 4
        });
        addText(item.reason || '', margin, {
          fontSize: 8,
          color: [51, 65, 85],
          lineHeight: 4
        });
        y += 2;
      });
    }

    pdf.save(`${exportFilenameBase}.pdf`);
  };

  return (
    <Card
      sx={{
        mb: 3,
        border: `1px solid ${border.light}`,
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
        borderRadius: 2
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 2
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: text.primary }}>
              Project Money Trail
            </Typography>
            <Typography variant="body2" sx={{ color: text.secondary }}>
              Trace funded, released, held, and outstanding project payments.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ minWidth: { md: 560 } }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="money-trail-project-label">Project</InputLabel>
              <Select
                labelId="money-trail-project-label"
                label="Project"
                value={selectedProjectId}
                onChange={handleProjectChange}
              >
                {normalizedProjects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              onClick={() => loadMoneyTrail()}
              disabled={!selectedProjectId || loading}
              sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportCsv}
              disabled={!moneyTrail || loading}
              sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
            >
              CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPdf}
              disabled={!moneyTrail || loading}
              sx={{
                whiteSpace: 'nowrap',
                textTransform: 'none',
                bgcolor: brand.primary,
                '&:hover': { bgcolor: brand.primaryDark }
              }}
            >
              PDF
            </Button>
          </Stack>
        </Box>

        {!selectedProjectId && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Select a project to see its complete money trail.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !moneyTrail && (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: brand.primary }} />
          </Box>
        )}

        {moneyTrail && (
          <Box>
            {hasWarnings && (
              <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
                {(summary.reconciliationWarnings || []).join(' ')}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {cards.map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.label}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderColor: border.light,
                      bgcolor: bg.paper,
                      minHeight: 124
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: text.secondary, fontWeight: 600 }}>
                        {card.label}
                      </Typography>
                      <Box sx={{ color: card.color, display: 'flex' }}>{card.icon}</Box>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: text.primary }}>
                      {formatCurrency(card.value, currency)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: text.secondary }}>
                      {card.helper}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderColor: border.light }}>
                  <Typography variant="body2" sx={{ color: text.secondary }}>Held for materials</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCurrency(summary.heldForMaterials, currency)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderColor: border.light }}>
                  <Typography variant="body2" sx={{ color: text.secondary }}>Milestones released</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCurrency(summary.releasedMilestonePayments, currency)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, borderColor: border.light }}>
                  <Typography variant="body2" sx={{ color: text.secondary }}>Materials released</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCurrency(summary.releasedMaterialPayments, currency)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Outstanding Payments
              </Typography>
              <Chip
                label={`${outstanding.length} open`}
                size="small"
                sx={{ bgcolor: status.warningBg, color: status.warningDark, fontWeight: 700 }}
              />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderColor: border.light }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: bg.subtle }}>
                    <TableCell>Belongs to</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outstanding.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" sx={{ color: text.secondary, py: 2, textAlign: 'center' }}>
                          No outstanding project payments.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    outstanding.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.recipientName || 'Recipient'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: text.secondary }}>
                            {item.recipientType || 'recipient'}
                          </Typography>
                        </TableCell>
                        <TableCell>{sourceLabels[item.source] || item.source}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>{item.reason}</TableCell>
                        <TableCell>
                          <Chip
                            label={(item.status || 'pending').replace(/_/g, ' ')}
                            color={statusColors[item.status] || 'default'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(item.dueDate)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCurrency(item.amount, item.currency || currency)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectMoneyTrail;
